import Colors from '@components/shared/Colors';
import { VMS_YELLOW_TICK_IMG } from '@constants';
import { PageSlice, SlicePositivesAndImageDetails, SlicePositivesAndImageItem } from '@contracts';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import styled from 'styled-components';
import LinkButton from './LinkButton';
import TwoColumn from './TwoColumn';
import { SFDBodyLGSFMBodyMD, SFDHeading2SFMHeading3 } from './Typography';

interface Props {
  slice: PageSlice;
}

const BREAKPOINT = '1200px';

const PositivesAndImage = ({ slice }: Props) => {
  const { t } = useTranslation();
  const sliceDetails = slice.primary as SlicePositivesAndImageDetails;
  const sliceTitle = t(sliceDetails.heading_text_key);
  const sliceItems = slice.items as SlicePositivesAndImageItem[];

  return (
    <Wrapper $mobileBackground={sliceDetails?.mobile_background_style} $background={sliceDetails?.background_style}>
      <TwoColumn breakpoint={BREAKPOINT} mobileReverse equal>
        <TextWrapper>
          <TitleWrapper>
            <SFDHeading2SFMHeading3>{sliceTitle}</SFDHeading2SFMHeading3>
          </TitleWrapper>
          <ItemsWrapper>
            {sliceItems.map((sliceItem) => (
              <ItemWrapper key={`idb-item-${sliceItem.main_text_key}`}>
                <ItemIconWrapper>
                  <LazyLoadImage src={VMS_YELLOW_TICK_IMG} alt={'yellow tick'} />
                </ItemIconWrapper>
                <ItemTextWrapper>
                  <SFDBodyLGSFMBodyMD>{t(sliceItem.main_text_key)}</SFDBodyLGSFMBodyMD>
                </ItemTextWrapper>
              </ItemWrapper>
            ))}
          </ItemsWrapper>
          {sliceDetails?.button_href && sliceDetails?.button_text_key ? (
            <ButtonWrapper>
              <LinkButton to={sliceDetails.button_href}>{t(sliceDetails.button_text_key)}</LinkButton>
            </ButtonWrapper>
          ) : null}
        </TextWrapper>
        <ImageWrapper>
          <LazyLoadImage src={sliceDetails?.image?.url} alt={sliceDetails?.image?.alt || ''} />
        </ImageWrapper>
      </TwoColumn>
    </Wrapper>
  );
};

export default PositivesAndImage;

const Wrapper = styled.div<{ $mobileBackground: string; $background: string }>`
  background: ${(props) => props.$background || Colors.babyBlue};

  @media (max-width: ${BREAKPOINT}) {
    padding: 0 20px;
    height: auto;
    background: ${(props) => props.$mobileBackground || 'none'};
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  max-width: 460px;

  a {
    display: inline-flex;
    border-radius: 5px;
  }

  @media (max-width: ${BREAKPOINT}) {
    display: block;
    margin-top: 10px;

    a {
      display: flex;
    }
  }
`;

const TextWrapper = styled.div`
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  padding: 80px 120px;
  gap: 30px;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  max-width: 50%;
  width: 100%;

  @media (max-width: ${BREAKPOINT}) {
    max-width: 100%;
    padding: 0;
    gap: 20px;
  }
`;

const TitleWrapper = styled.div`
  max-width: 460px;
  display: flex;
  justify-content: center;

  @media (max-width: ${BREAKPOINT}) {
    text-align: center;
    margin-top: 20px;
  }
`;

const ItemsWrapper = styled.div`
  max-width: 460px;
  display: flex;
  gap: 15px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${BREAKPOINT}) {
    gap: 10px;
    padding: 0 10px 0 15px;
  }
`;

const ItemWrapper = styled.div`
  display: flex;
  gap: 15px;
`;

const ItemIconWrapper = styled.div`
  align-items: start;
  display: flex;

  img {
    width: 15px;
    max-width: 15px;
  }
`;

const ItemTextWrapper = styled.div``;

const ImageWrapper = styled.div`
  width: 740px;
  max-width: 50%;
  flex-grow: 1;

  img {
    width: 100%;
  }

  @media (max-width: ${BREAKPOINT}) {
    border-radius: 10px;
    overflow: hidden;
    width: 100%;
    max-width: 500px;

    img {
      max-width: 100%;
    }
  }
`;
